import { isEmpty, isNil, map, toPairs } from 'lodash';

import { SigDraftAction, SigDraftItemLabel } from 'enums/SigManagement.enum';

import describeCatalogItem from './describeCatalogItem';

/**
 * How the staged draft is folded onto what the server actually returned.
 *
 * The cards render from the server's rows with the draft laid over the top, never from the draft
 * alone. That way a row nobody touched keeps whatever the server says even if it changed underneath
 * them, and a staged row is visibly staged rather than indistinguishable from a fact.
 *
 * `isStaged` means "this row's status differs from the server's", which is what the commit will
 * write. Toggling a row back to where it started still leaves an entry in the draft, so the flag is
 * derived by comparing rather than by asking whether an entry exists.
 */
const withStagedStatus = (rows, statusEdits, readId) =>
  map(rows, (row) => {
    const stagedStatus = statusEdits[readId(row)];
    const isStaged = !isNil(stagedStatus) && stagedStatus !== row.isActive;

    return {
      ...row,
      isActive: isNil(stagedStatus) ? row.isActive : stagedStatus,
      isStaged,
      // Kept alongside the effective status so a staged row can say what it is changing FROM.
      // "Inactive" on its own reads as already inactive, which is the opposite of pending.
      serverIsActive: row.isActive,
    };
  });

export const applyDraftToDirections = (directions, sigDraft) =>
  withStagedStatus(
    directions,
    sigDraft?.directionStatusEdits ?? {},
    ({ directionId }) => directionId
  );

export const applyDraftToCatalogItems = (catalogItems, sigDraft) =>
  withStagedStatus(catalogItems, sigDraft?.catalogStatusEdits ?? {}, ({ catalogId }) => catalogId);

const toStatusChange = ({ key, item, isActive, label, detail, undo }) => ({
  key,
  item,
  label,
  detail,
  undo,
  action: isActive ? SigDraftAction.SET_ACTIVE : SigDraftAction.SET_INACTIVE,
  fromIsActive: !isActive,
  toIsActive: isActive,
});

/**
 * One flat list of what the commit will write, for the pending-changes card.
 *
 * A status edit that matches what the server already has is left out: the user toggled a row and
 * toggled it back, so there is nothing to commit and listing it would claim otherwise.
 *
 * Each entry carries an `undo` descriptor rather than a callback, so this stays a pure function the
 * card can render and a test can assert without a store.
 */
export const describeStagedChanges = ({
  sig,
  directions,
  catalogItems,
  sigDraft,
  isCreating = false,
}) => {
  if (isNil(sigDraft)) {
    return [];
  }

  const directionsById = new Map(map(directions, (row) => [row.directionId, row]));
  const catalogItemsById = new Map(map(catalogItems, (row) => [row.catalogId, row]));

  // Not listed when the sig is being created: there is nothing it is changing FROM, and the text is
  // already on screen in the editor above. Listed as "Edit sig / was <old text>" it would claim an
  // edit to a record that does not exist.
  const sigTextChange =
    !isCreating && !isEmpty(sigDraft.sigText) && sigDraft.sigText !== sig
      ? [
          {
            key: 'sig-text',
            action: SigDraftAction.EDIT_SIG,
            item: SigDraftItemLabel.SIG,
            label: sigDraft.sigText,
            fromText: sig,
            undo: { kind: 'sigText' },
          },
        ]
      : [];

  const addedDirections = map(sigDraft.newDirections ?? [], ({ draftId, direction }) => ({
    key: `new-direction-${draftId}`,
    action: SigDraftAction.ADD,
    item: SigDraftItemLabel.DIRECTION,
    label: direction,
    undo: { kind: 'newDirection', draftId },
  }));

  const addedCatalogItems = map(sigDraft.newCatalogItems ?? [], (catalogItem) => ({
    key: `new-catalog-${catalogItem.catalogId}`,
    action: SigDraftAction.ADD,
    item: SigDraftItemLabel.CATALOG_ITEM,
    label: catalogItem.drugName,
    detail: describeCatalogItem(catalogItem),
    undo: { kind: 'newCatalogItem', catalogId: catalogItem.catalogId },
  }));

  const directionStatusChanges = toPairs(sigDraft.directionStatusEdits ?? {})
    .filter(([directionId, isActive]) => directionsById.get(directionId)?.isActive !== isActive)
    .map(([directionId, isActive]) =>
      toStatusChange({
        key: `direction-${directionId}`,
        item: SigDraftItemLabel.DIRECTION,
        isActive,
        label: directionsById.get(directionId)?.direction ?? directionId,
        undo: { kind: 'directionStatus', directionId },
      })
    );

  const catalogStatusChanges = toPairs(sigDraft.catalogStatusEdits ?? {})
    .filter(([catalogId, isActive]) => catalogItemsById.get(catalogId)?.isActive !== isActive)
    .map(([catalogId, isActive]) => {
      const catalogItem = catalogItemsById.get(catalogId);

      return toStatusChange({
        key: `catalog-${catalogId}`,
        item: SigDraftItemLabel.CATALOG_ITEM,
        isActive,
        label: catalogItem?.drugName ?? catalogId,
        detail: catalogItem && describeCatalogItem(catalogItem),
        undo: { kind: 'catalogStatus', catalogId },
      });
    });

  // The sig first, then additions, then status changes — roughly the order the work is done in, and
  // it puts the change with the widest reach at the top.
  return [
    ...sigTextChange,
    ...addedDirections,
    ...addedCatalogItems,
    ...directionStatusChanges,
    ...catalogStatusChanges,
  ];
};
